/**
 * Pure, framework-free helpers for the app's authentication boot sequence.
 *
 * No React, no Supabase, no DOM globals — every browser storage access is
 * injected via the StorageLike interface so this module is trivially unit
 * testable under plain Node (see tests/auth-boot/auth-boot.test.ts).
 *
 * Wired into the actual boot effect in
 * src/components/providers/AuthProvider.tsx.
 */

export const PROFILE_SNAPSHOT_PREFIX = "buildsynx:profile:v1:";

export type StorageLike = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
    key(index: number): string | null;
    readonly length: number;
};

function snapshotKey(userId: string): string {
    return `${PROFILE_SNAPSHOT_PREFIX}${userId}`;
}

/**
 * Reads a previously-cached profile snapshot for `userId`. Returns null when
 * the key is missing, the stored value isn't valid JSON, or storage access
 * throws for any reason (private-mode Safari, disabled storage, etc). Never
 * throws.
 */
export function readProfileSnapshot<T = unknown>(storage: StorageLike | null | undefined, userId: string): T | null {
    if (!storage) return null;
    try {
        const raw = storage.getItem(snapshotKey(userId));
        if (raw === null) return null;
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

/**
 * Caches a verified profile for `userId` so the next app boot can restore it
 * optimistically. Swallows quota-exceeded / private-mode write errors —
 * caching is a nice-to-have for a faster boot, never a hard requirement.
 * Never throws.
 */
export function writeProfileSnapshot(storage: StorageLike | null | undefined, userId: string, profile: unknown): void {
    if (!storage) return;
    try {
        storage.setItem(snapshotKey(userId), JSON.stringify(profile));
    } catch {
        // Quota exceeded, storage disabled, or a private-mode browser — non-critical.
    }
}

/**
 * Removes every cached profile snapshot this module has ever written
 * (any user), leaving every other key in storage untouched. Never throws.
 */
export function clearProfileSnapshots(storage: StorageLike | null | undefined): void {
    if (!storage) return;
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key && key.startsWith(PROFILE_SNAPSHOT_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        for (const key of keysToRemove) {
            try {
                storage.removeItem(key);
            } catch {
                // Best effort — keep clearing the rest even if one key fails.
            }
        }
    } catch {
        // storage.length or storage.key threw — nothing more we can do.
    }
}

export type BootState = {
    status: "restoring" | "authed-unverified" | "authed-verified" | "anon";
    user: unknown | null;
    profile: unknown | null;
};

/**
 * Decides the initial boot state from a local (unverified) session read plus
 * whatever profile snapshot is cached for that user's id. A local user AND a
 * matching snapshot are BOTH required to show restored data immediately — a
 * local user with no snapshot yet (first-ever login on this device) falls
 * back to "restoring" so the caller keeps today's loading-screen behavior
 * instead of guessing at a profile it has never seen.
 */
export function resolveBootState(input: { localUser: { id: string } | null | undefined; snapshot: unknown | null }): BootState {
    if (input.localUser && input.snapshot) {
        return { status: "authed-unverified", user: input.localUser, profile: input.snapshot };
    }
    return { status: "restoring", user: null, profile: null };
}

/** The server confirmed the session and returned a profile — the trusted end state. */
export function applyVerificationSuccess(input: { user: { id: string }; profile: unknown }): BootState {
    return { status: "authed-verified", user: input.user, profile: input.profile };
}

/** The server found no session — any optimistic restore was wrong; caller should treat this as signed out. */
export function applyVerificationFailure(): BootState {
    return { status: "anon", user: null, profile: null };
}

/**
 * supabase-js awaits the onAuthStateChange callback while holding its
 * internal auth lock (see GoTrueClient#_acquireLock's pendingInLock drain),
 * so an async callback body blocks every concurrent getSession()/getUser()
 * call — including bootAuth's own — until that callback's promise settles.
 * The fix keeps the callback itself synchronous; this helper then decides,
 * from inside the detached async handler, whether the event it just received
 * is the exact initial session bootAuth is already restoring/verifying (in
 * which case bootAuth owns every state write and the handler should do
 * nothing but keep Sentry's identity in sync) or genuinely new work (a later
 * sign-in, sign-out, account switch, token refresh) that still needs full
 * handling.
 */
export function shouldDeferToBoot(input: {
    bootInFlight: boolean;
    event: string;
    eventUserId: string | null;
    bootUserId: string | null;
}): boolean {
    return (
        input.bootInFlight &&
        (input.event === "INITIAL_SESSION" || input.event === "SIGNED_IN") &&
        input.eventUserId !== null &&
        input.eventUserId === input.bootUserId
    );
}
